import {
  iconClasses,
  styled,
  svgIconClasses,
  useThemeProps,
} from '@mui/material';
import {
  default as MuiLink,
  LinkTypeMap as MuiLinkTypeMap,
} from '@mui/material/Link';
import { OverrideProps } from '@mui/material/OverridableComponent';
import * as React from 'react';
import { useIsAncestorDisabled } from 'src/context/DisabledAncestorContext/DisabledAncestorContext';
import {
  ExtendTypeMap,
  forwardRefToOverridableComponent,
} from 'src/utils/forwardRefToOverridableComponent';
import linkClasses from './linkClasses';
import { useLegacyWsdStyles } from 'src/wsd/styles/context/useLegacyWsdStyles';

export interface LinkOwnProps {
  /**
   * @deprecated Use the `color` property in `sx` with `getToken` instead.
   */
  color?: MuiLinkTypeMap['props']['color'];

  /**
   * Icon element placed after the children.
   */
  endIcon?: React.ReactNode;
}

export type LinkTypeMap<
  AdditionalProps = unknown,
  RootComponent extends React.ElementType = MuiLinkTypeMap['defaultComponent']
> = ExtendTypeMap<
  MuiLinkTypeMap,
  AdditionalProps & LinkOwnProps,
  RootComponent
>;

export type LinkProps<
  RootComponent extends React.ElementType = MuiLinkTypeMap['defaultComponent'],
  AdditionalProps = unknown
> = OverrideProps<LinkTypeMap<AdditionalProps, RootComponent>, RootComponent>;

/**
 * This styled component is only rendered when {@link unifyStyleContext.legacyWsdStyleSupport} is `true`.
 */
const WsdLinkRoot = styled(MuiLink, {
  name: 'WsdLink',
  slot: 'Root',
})`
  color: #026dce;
  text-decoration-color: #026dce;
  &:hover {
    color: #0094ff;
    text-decoration-color: #0094ff;
  }
`;

/**
 * Links are typographical navigational elements that appear within or following a paragraph or sentence.
 *
 * Demos:
 *
 * - [Link](https://unify.workiva.org/components/link?tab=code)
 *
 * API:
 *
 * - [Link API](https://unify.workiva.org/components/link?tab=props)
 */
export const Link = forwardRefToOverridableComponent<LinkTypeMap, LinkProps>(
  function Link(inProps, ref) {
    const isWsdLink = useLegacyWsdStyles();
    const props = useThemeProps({ props: inProps, name: 'MuiLink' });
    const { children, color: colorProp, endIcon, ...rest } = props; // eslint-disable-line deprecation/deprecation
    const ancestorDisabled = useIsAncestorDisabled();
    const color = ancestorDisabled ? 'inherit' : colorProp;

    const Component = isWsdLink ? WsdLinkRoot : MuiLink;

    return (
      <Component
        sx={endIcon ? { display: 'flex', alignItems: 'center' } : {}}
        color={color}
        {...rest}
        ref={ref}
      >
        {children}
        {endIcon ? (
          <LinkEndIcon className={linkClasses.endIcon}>{endIcon}</LinkEndIcon>
        ) : undefined}
      </Component>
    );
  }
);

const LinkEndIcon = styled('span', {
  name: 'MuiLink',
  slot: 'EndIcon',
  overridesResolver: (props, styles) => styles.endIcon,
})(() => ({
  display: 'inherit',
  marginLeft: 8,
  [`& .${svgIconClasses.root}, & .${iconClasses.root}`]: {
    fontSize: '20px',
  },
}));

export default Link;
